import classNames from 'classnames'
import { JSX, useEffect, useRef, useState } from 'react'

import { Portal } from 'core-components/library/popup/notification/NotificationPortal'

import styles from './Notification.module.scss'

interface IContentData {
  close: () => void
}

const ANIMATION_DURATION = 250

export interface INotificationPopup {
  /**
   * Notification template.
   */
  content: (contentData: IContentData) => JSX.Element
  /**
   * Enable auto hiding notify popup.
   */
  autoHide?: boolean
  /**
   * Lifetime in ms of notify popup with autoHide property enabled.
   */
  autoHideDuration?: number
  /**
   * Handler for closing notify popup.
   */
  onClose?: () => void
  /**
   * Vertical position of notify popup.
   */
  verticalPosition?: 'top' | 'bottom'
  /**
   * Horizontal position of notify popup.
   */
  horizontalPosition?: 'left' | 'right'
}

export const Notification = ({
  content,
  autoHide = true,
  autoHideDuration = 6000,
  onClose = () => undefined,
  verticalPosition = 'bottom',
  horizontalPosition = 'right',
}: INotificationPopup) => {
  const timer = useRef<number>(0)
  const [visible, setVisible] = useState<boolean>(true)
  const [mounted, setMounted] = useState<boolean>()
  const contentData = {
    close: () => setMounted(false),
  }

  useEffect(() => {
    if (!visible) {
      onClose()
    } else {
      if (autoHide) {
        if (!timer.current) {
          timer.current = window.setTimeout(() => setMounted(false), autoHideDuration)
        }
      }
    }
  }, [onClose, visible, autoHide])

  useEffect(() => {
    setMounted(true)
    return () => {
      if (timer.current) {
        clearTimeout(timer.current)
      }
    }
  }, [])

  useEffect(() => {
    if (mounted === false) {
      setTimeout(() => setVisible(false), ANIMATION_DURATION)
    }
  }, [mounted])

  return (
    <Portal verticalPosition={verticalPosition} horizontalPosition={horizontalPosition}>
      <div
        data-testid={'notification-wrapper'}
        onTransitionEnd={() => {
          if (mounted === false) {
            setVisible(false)
          }
        }}
        className={classNames(
          styles.NotificationPopup,
          styles[`NotificationPopup_state-${visible && mounted ? 'visible' : 'hidden'}`]
        )}
      >
        {visible ? content(contentData) : <></>}
      </div>
    </Portal>
  )
}
